import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store';
import { getVoiceAgentBaseUrl, handleErrorResponse } from '../api';
import type {
  VoiceAgent,
  VoiceAgentListResponse,
  CreateVoiceAgentRequest,
  AllowedModelsResponse,
  PhoneNumberListResponse,
  InboundAttachResponse,
  InboundAttachListResponse,
  AgentResponse,
  ListIntegrationsResponse,
  ProviderListResponse,
  PhoneNumberAvailableResponse,
  PhoneNumberPurchaseResponse,
  OutboundCallRequest,
  OutboundCallResponse,
  WebRtcSessionRequest,
  WebRtcSessionResponse,
  VoiceAgentVersionListItem,
  VoiceAgentVersionDetail,
  PronunciationDictionary,
  PronunciationDictionaryQueryParams,
  PronunciationDictionaryListPaginatedResponse,
  CreatePronunciationDictionaryRequest,
  UpdatePronunciationDictionaryItemRequest,
  DeletePronunciationDictionaryItemRequest,
  ExportPronunciationDictionaryRequest,
  PronunciationDictionaryExportResponse,
  ImportPronunciationDictionaryRequest,
  PronunciationDictionaryImportResponse,
} from './voiceAgentTypes';
import {
  normalizePronunciationDictionary,
  normalizePronunciationDictionaryListResponse,
  normalizePronunciationDictionaryListPaginatedResponse,
} from './pronunciationDictionaryUtils';

/** Normalize list endpoint: array, `{ versions }`, or `{ assistant_id, current_version, versions }`. */
function normalizeAgentVersionsResponse(response: unknown): VoiceAgentVersionListItem[] {
  if (Array.isArray(response)) {
    return response as VoiceAgentVersionListItem[];
  }
  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as { versions?: unknown }).versions)
  ) {
    return (response as { versions: VoiceAgentVersionListItem[] }).versions;
  }
  return [];
}

/** Resolved agentconfig API base for the current `getVoiceAgentBaseUrl()` (for logging or diagnostics). */
export function getVoiceAgentApiBaseUrl(): string {
  const h = getVoiceAgentBaseUrl();
  return h ? `${h}/agentconfig/api` : '';
}

const voiceAgentPrepareHeaders = (headers: Headers, { getState }: { getState: () => unknown }) => {
  const token = (getState() as RootState)?.auth?.token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('id_token', token);
  }
  return headers;
};

const voiceAgentInnerBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: voiceAgentPrepareHeaders,
});

const voiceAgentDynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const host = getVoiceAgentBaseUrl();
  if (!host) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Voice agent base URL is not configured. Call setVoiceAgentConfig({ baseUrl }) from your app entry with CTY_VOICE_AGENT_BASE_URL.',
      } as FetchBaseQueryError,
    };
  }
  const base = `${host}/agentconfig/api`;
  const path = typeof args === 'string' ? args : args.url;
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const adjusted = typeof args === 'string' ? url : { ...args, url };
  return voiceAgentInnerBaseQuery(adjusted, api, extraOptions);
};

export const voiceAgentApi = createApi({
  reducerPath: 'voiceAgentApi',
  tagTypes: ['VoiceAgent', 'InboundAttach', 'PhoneNumber', 'PronunciationDictionary'],
  baseQuery: voiceAgentDynamicBaseQuery,
  endpoints: (builder) => ({
    getAgent: builder.query<VoiceAgent[], void>({
      query: () => '/agent',
      transformResponse: (response: VoiceAgentListResponse) => response.data,
      transformErrorResponse: handleErrorResponse,
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'VoiceAgent' as const, id })),
            { type: 'VoiceAgent', id: 'LIST' },
          ]
          : [{ type: 'VoiceAgent', id: 'LIST' }],
    }),
    getAgentById: builder.query<AgentResponse, string>({
      query: (assistantId) => `/agent/${assistantId}`,
      transformErrorResponse: handleErrorResponse,
      providesTags: (_result, _error, id) => [{ type: 'VoiceAgent', id }],
    }),
    getAllowedModels: builder.query<AllowedModelsResponse, void>({
      query: () => ({
        url: '/allowed-models',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    createAgent: builder.mutation<VoiceAgent, CreateVoiceAgentRequest>({
      query: (body) => ({
        url: '/agent',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: 'VoiceAgent', id: 'LIST' }],
    }),
    updateAgent: builder.mutation<
      AgentResponse,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/agent/${id}`,
        method: 'PATCH',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'VoiceAgent', id },
        { type: 'VoiceAgent', id: 'LIST' },
      ],
    }),
    getPhoneNumberList: builder.query<
      PhoneNumberListResponse,
      { provider?: string; country_iso?: string } | void
    >({
      query: (params) => ({
        url: 'phonenumber/list',
        ...(params && {
          params: { provider: params.provider, country_iso: params.country_iso },
        }),
      }),
      transformErrorResponse: handleErrorResponse,
      providesTags: ['PhoneNumber'],
    }),
    getInboundAttachList: builder.query<
      InboundAttachListResponse,
      string
    >({
      query: (assistantId) => ({
        url: 'inboundattach/list',
        params: { assistant_id: assistantId },
      }),
      transformResponse: (response: unknown): InboundAttachListResponse => {
        if (Array.isArray(response)) {
          return { numbers: response };
        }
        return response as InboundAttachListResponse;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (_result, _error, assistantId) => [
        { type: 'InboundAttach', id: assistantId },
      ],
    }),
    postInboundAttach: builder.mutation<
      InboundAttachResponse,
      { phone_number?: string; assistant_id?: string; provider?: string }
    >({
      query: (args) => ({
        url: 'inboundattach',
        method: 'POST',
        params: {
          ...(args.assistant_id != null && { assistant_id: args.assistant_id }),
          ...(args.phone_number != null && { phone_number: args.phone_number }),
          ...(args.provider != null && { provider: args.provider }),
        },
      }),
      invalidatesTags: (_result, _error, { assistant_id }) =>
        assistant_id
          ? [{ type: 'InboundAttach', id: assistant_id }]
          : [],
    }),
    deleteInboundAttach: builder.mutation<
      InboundAttachResponse,
      { phone_number?: string; assistant_id?: string; provider?: string }
    >({
      query: (args) => ({
        url: 'inboundattach',
        method: 'DELETE',
        params: {
          ...(args.assistant_id != null && { assistant_id: args.assistant_id }),
          ...(args.phone_number != null && { phone_number: args.phone_number }),
          ...(args.provider != null && { provider: args.provider }),
        },
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { assistant_id }) =>
        assistant_id
          ? [{ type: 'InboundAttach', id: assistant_id }]
          : [],
    }),
    getListIntegrations: builder.query<ListIntegrationsResponse, void>({
      query: () => 'list-integrations',
      transformErrorResponse: handleErrorResponse,
    }),
    getProviderList: builder.query<ProviderListResponse, void>({
      query: () => 'provider/list',
      transformErrorResponse: handleErrorResponse,
    }),
    getPhoneNumberAvailable: builder.query<
      PhoneNumberAvailableResponse,
      { provider?: string; country_iso?: string }
    >({
      query: (params) => ({
        url: 'phonenumber/available',
        params,
      }),
      transformResponse: (response: unknown): PhoneNumberAvailableResponse => {
        if (Array.isArray(response)) {
          return { numbers: response };
        }
        return response as PhoneNumberAvailableResponse;
      },
      transformErrorResponse: handleErrorResponse,
    }),
    postPhoneNumberPurchase: builder.mutation<
      PhoneNumberPurchaseResponse,
      { provider: string; country_iso: string; body?: Record<string, unknown> }
    >({
      query: ({ provider, country_iso, body }) => ({
        url: 'phonenumber/purchase',
        method: 'POST',
        params: { provider, country_iso },
        body: body ?? {},
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: ['PhoneNumber'],
    }),
    postOutboundCall: builder.mutation<OutboundCallResponse, OutboundCallRequest>({
      query: (body) => ({
        url: 'agentcall/outbound-call',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    postWebRtcSession: builder.mutation<WebRtcSessionResponse, WebRtcSessionRequest>({
      query: (body) => ({
        url: 'agentcall/web_RTC',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `agent/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: 'VoiceAgent', id: 'LIST' }],
    }),
    getAgentVersions: builder.query<VoiceAgentVersionListItem[], string>({
      query: (assistantId) => `agent/${assistantId}/versions`,
      transformResponse: normalizeAgentVersionsResponse,
      transformErrorResponse: handleErrorResponse,
      providesTags: (_result, _error, id) => [{ type: 'VoiceAgent', id }],
    }),
    getAgentVersionDetail: builder.query<
      VoiceAgentVersionDetail,
      { assistantId: string; version: string }
    >({
      query: ({ assistantId, version }) => ({
        url: `agent/${assistantId}/versions`,
        params: { version },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    restoreAgentVersion: builder.mutation<
      AgentResponse,
      { assistantId: string; version: number }
    >({
      query: ({ assistantId, version }) => ({
        url: `agent/${assistantId}/versions/restore`,
        method: 'POST',
        body: { version },
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { assistantId }) => [
        { type: 'VoiceAgent', id: assistantId },
        { type: 'VoiceAgent', id: 'LIST' },
      ],
    }),
    postVoicePreview: builder.mutation<
      Blob,
      { transcript: string; voiceId: string; provider?: string }
    >({
      query: ({ transcript, voiceId, provider }) => {
        const transcriptNorm = transcript.trim()
        const providerNorm = String(provider ?? '').trim().toLowerCase();
        const body: Record<string, string> = { transcript: transcriptNorm };
        if (providerNorm === 'azure' || providerNorm === 'openai') {
          body.voice = voiceId;
        } else {
          body.voice_id = voiceId;
        }
        if (String(provider ?? '').trim() !== '') {
          body.provider = String(provider).trim();
        }
        return {
          url: 'voice-preview',
          method: 'POST',
          body,
          responseHandler: (response: Response) => response.blob(),
        };
      },
      transformErrorResponse: handleErrorResponse,
    }),
    deleteVoiceAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `agent/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: 'VoiceAgent', id: 'LIST' }],
    }),
    getPronunciationDictionaries: builder.query<
      PronunciationDictionaryListPaginatedResponse,
      PronunciationDictionaryQueryParams | void
    >({
      query: (params) => ({
        url: 'pronunciation-dictionaries',
        params: {
          page: params?.page ?? 1,
          per_page: params?.per_page ?? 10,
        },
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePronunciationDictionaryListPaginatedResponse(
          response,
          arg?.per_page ?? 10
        ),
      transformErrorResponse: handleErrorResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ name }) => ({
                type: 'PronunciationDictionary' as const,
                id: name ?? 'UNKNOWN',
              })),
              { type: 'PronunciationDictionary', id: 'LIST' },
            ]
          : [{ type: 'PronunciationDictionary', id: 'LIST' }],
    }),
    getPronunciationDictionaryByName: builder.query<PronunciationDictionary | null, string>({
      query: (name) => ({
        url: 'pronunciation-dictionaries',
        params: { name },
      }),
      transformResponse: (response: unknown) => {
        const list = normalizePronunciationDictionaryListResponse(response);
        return list[0] ?? null;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: (_result, _error, name) => [
        { type: 'PronunciationDictionary', id: name },
      ],
    }),
    createPronunciationDictionary: builder.mutation<
      PronunciationDictionary,
      CreatePronunciationDictionaryRequest
    >({
      query: (body) => ({
        url: 'pronunciation-dictionaries',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const list = normalizePronunciationDictionaryListResponse(response);
        if (list[0]) return list[0];
        return normalizePronunciationDictionary(response);
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: 'PronunciationDictionary', id: 'LIST' }],
    }),
    updatePronunciationDictionaryItem: builder.mutation<
      PronunciationDictionary,
      UpdatePronunciationDictionaryItemRequest
    >({
      query: (body) => ({
        url: 'pronunciation-dictionaries',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => {
        const list = normalizePronunciationDictionaryListResponse(response);
        if (list[0]) return list[0];
        return normalizePronunciationDictionary(response);
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'PronunciationDictionary', id: name },
        { type: 'PronunciationDictionary', id: 'LIST' },
      ],
    }),
    deletePronunciationDictionaryItem: builder.mutation<
      void,
      DeletePronunciationDictionaryItemRequest
    >({
      query: ({ dictionaryName, text }) => ({
        url: 'pronunciation-dictionaries/item',
        method: 'DELETE',
        params: { name: dictionaryName, text },
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { dictionaryName }) => [
        { type: 'PronunciationDictionary', id: dictionaryName },
        { type: 'PronunciationDictionary', id: 'LIST' },
      ],
    }),
    deletePronunciationDictionary: builder.mutation<void, string>({
      query: (name) => ({
        url: 'pronunciation-dictionaries',
        method: 'DELETE',
        params: { name },
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: [{ type: 'PronunciationDictionary', id: 'LIST' }],
    }),
    exportPronunciationDictionary: builder.mutation<
      PronunciationDictionaryExportResponse,
      ExportPronunciationDictionaryRequest
    >({
      query: ({ dictionaryId, format = 'array' }) => ({
        url: `pronunciation-dictionaries/${encodeURIComponent(dictionaryId)}/export`,
        params: { format },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    importPronunciationDictionaryItems: builder.mutation<
      PronunciationDictionaryImportResponse,
      ImportPronunciationDictionaryRequest
    >({
      query: ({ dictionaryId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `pronunciation-dictionaries/${encodeURIComponent(dictionaryId)}/import`,
          method: 'POST',
          body: formData,
        };
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, { dictionaryName }) => [
        { type: 'PronunciationDictionary', id: dictionaryName },
        { type: 'PronunciationDictionary', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAgentQuery,
  useGetAgentByIdQuery,
  useGetAllowedModelsQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useGetPhoneNumberListQuery,
  useGetInboundAttachListQuery,
  usePostInboundAttachMutation,
  useDeleteInboundAttachMutation,
  useGetListIntegrationsQuery,
  useGetProviderListQuery,
  useGetPhoneNumberAvailableQuery,
  usePostPhoneNumberPurchaseMutation,
  usePostOutboundCallMutation,
  usePostWebRtcSessionMutation,
  useDeleteAgentMutation,
  useGetAgentVersionsQuery,
  useGetAgentVersionDetailQuery,
  useRestoreAgentVersionMutation,
  usePostVoicePreviewMutation,
  useDeleteVoiceAgentMutation,
  useGetPronunciationDictionariesQuery,
  useGetPronunciationDictionaryByNameQuery,
  useCreatePronunciationDictionaryMutation,
  useUpdatePronunciationDictionaryItemMutation,
  useDeletePronunciationDictionaryItemMutation,
  useDeletePronunciationDictionaryMutation,
  useExportPronunciationDictionaryMutation,
  useImportPronunciationDictionaryItemsMutation,
} = voiceAgentApi;
